import type { Auth } from 'firebase/auth';
import {
  type Firestore,
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';

import type {
  DependentPort,
  MyDependent,
  NewDependent,
} from '../../features/employees/application/ports/dependentPort';
import { DependentError } from '../../features/employees/application/ports/dependentPort';

/**
 * Firestore implementation of `DependentPort` (blueprint §11.1).
 *
 * Raw platform errors are translated and never escape (§4.5).
 */

/**
 * Hard ceiling on a single read.
 *
 * The fund policy caps dependants far below this, so the limit should never
 * bite — which is exactly why it is here. An unbounded query is a cost defect
 * on a billing account with no hard cap (§11.5.1), and "the policy will stop
 * it" is a guarantee made somewhere else.
 */
const MAX_DEPENDENTS_READ = 50;

function translate(error: unknown): DependentError {
  const code =
    typeof error === 'object' && error !== null && 'code' in error ? String(error.code) : '';

  if (code.includes('permission-denied')) {
    // The rules refuse a write when submissions are closed or the record has
    // moved past pending. Both look identical from here, so the message covers
    // the two honestly rather than guessing.
    return new DependentError(
      'not_editable',
      'لا يمكن التعديل الآن. قد تكون فترة التقديم مغلقة أو تمت مراجعة البيانات.',
    );
  }
  if (code.includes('unavailable') || code.includes('deadline')) {
    return new DependentError('network', 'تعذّر الاتصال. تحقق من الإنترنت وحاول مجددًا.');
  }
  return new DependentError('unknown', 'تعذّر إتمام العملية. حاول مرة أخرى.');
}

function readString(data: Record<string, unknown>, key: string): string | null {
  const value = data[key];
  return typeof value === 'string' && value !== '' ? value : null;
}

export function createFirebaseDependentAdapter(
  firestore: Firestore,
  auth: Auth,
): DependentPort {
  async function resolveMyEmployeeId(): Promise<string> {
    const uid = auth.currentUser?.uid;
    if (!uid) throw new DependentError('forbidden', 'يجب تسجيل الدخول أولًا.');

    const link = await getDoc(doc(firestore, 'userAccounts', uid));
    const employeeId: unknown = link.data()?.['employeeId'];

    if (typeof employeeId !== 'string') {
      throw new DependentError('forbidden', 'لا يوجد سجل موظف مرتبط بحسابك.');
    }
    return employeeId;
  }

  return {
    async listMyDependents(): Promise<readonly MyDependent[]> {
      try {
        const employeeId = await resolveMyEmployeeId();

        const snapshot = await getDocs(
          query(
            collection(firestore, 'dependents'),
            where('employeeId', '==', employeeId),
            limit(MAX_DEPENDENTS_READ),
          ),
        );

        return snapshot.docs.map((entry) => {
          const data = entry.data();
          const gender = readString(data, 'gender');

          return {
            id: entry.id,
            relation: (readString(data, 'relation') ?? 'son') as MyDependent['relation'],
            fullName: readString(data, 'fullName') ?? '',
            gender: gender === 'female' ? 'female' : 'male',
            birthDate: readString(data, 'birthDate') ?? '',
            nationalId: readString(data, 'nationalId'),
            status: (readString(data, 'status') ??
              'pending_documents') as MyDependent['status'],
            proofDocumentId: readString(data, 'proofDocumentId'),
          };
        });
      } catch (error) {
        throw error instanceof DependentError ? error : translate(error);
      }
    },

    async addDependent(dependent: NewDependent): Promise<void> {
      try {
        const employeeId = await resolveMyEmployeeId();
        const now = new Date().toISOString();

        // `employeeId` and `status` are set here to exactly what the rules
        // require. An employee cannot claim a dependant belongs to someone
        // else, and cannot create one already active.
        await addDoc(collection(firestore, 'dependents'), {
          schemaVersion: 1,
          employeeId,
          relation: dependent.relation,
          fullName: dependent.fullName,
          gender: dependent.gender,
          birthDate: dependent.birthDate,
          ...(dependent.nationalId === undefined ? {} : { nationalId: dependent.nationalId }),
          status: 'pending_documents',
          createdAt: now,
          updatedAt: now,
          updatedBy: employeeId,
        });
      } catch (error) {
        throw error instanceof DependentError ? error : translate(error);
      }
    },

    async updateDependent(id: string, dependent: NewDependent): Promise<void> {
      try {
        const employeeId = await resolveMyEmployeeId();

        // Exactly the owner-editable fields from firestore.rules. `status` and
        // `employeeId` are absent, so an edit cannot change ownership or
        // approval — the same list in two places, on purpose.
        await updateDoc(doc(firestore, 'dependents', id), {
          fullName: dependent.fullName,
          gender: dependent.gender,
          birthDate: dependent.birthDate,
          ...(dependent.nationalId === undefined ? {} : { nationalId: dependent.nationalId }),
          updatedAt: new Date().toISOString(),
          updatedBy: employeeId,
        });
      } catch (error) {
        throw error instanceof DependentError ? error : translate(error);
      }
    },
  };
}
